import customAxios from "config";
import toast from "react-hot-toast";
import { renterError } from "utils/errorMessage";

/**
 * 1NOW-10770: with `"email_code": true` a returning renter's email gets no session but a code in their inbox:
 * `{ code_required: true, detail, resend_after }`. Asked again too soon (no code works yet) the server says so with
 * a 400 that still carries `code_required`; that comes back as `data` too, its words in `data.error`, so a caller
 * has one shape to read: the code question, with the wait. The same body plus `code` answers with the full
 * session, or a 400 whose words are the `error`.
 */
export const AddCustomer = async (payload: any): Promise<any> => {
  try {
    const response = await customAxios.post<any>(
      "/api/company/add-website-customer/",
      payload
    );
    return { data: response.data };
  } catch (error: any) {
    const body = error?.response?.data;
    if (body?.code_required === true)
      return {
        data: {
          code_required: true,
          resend_after: body.resend_after,
          error: renterError(error, "auth"),
        },
      };
    // Was `return error.response` — the raw axios response object, with no
    // renter-safe copy. A blocked identity's refusal (1NOW-10512) now
    // surfaces through the normalized { error } shape every call site checks.
    return { error: renterError(error, "auth") };
  }
};

/**
 * The renter session for a booking, proved by the booking's uuid (1NOW-10770).
 *
 * The edit screen used to get it from the renter's email and the company id,
 * both guessable, and got the renter's full session back. This gives a token
 * that reaches this one booking. POST, so the uuid stays out of access logs,
 * browser history and the Referer header.
 */
export const getRenterSessionForBooking = async (
  bookingUuid: string
): Promise<any> => {
  try {
    const response = await customAxios.post(
      "/api/company/booking-renter-session/",
      { booking: bookingUuid }
    );
    return response.data;
  } catch (error) {
    console.error("Booking renter session call failed:", error);
    return;
  }
};

export const getCustomerFiles = async (
  customer_id: number | string,
  related_to: string
): Promise<{ data?: any; error?: string }> => {
  try {
    const response = await customAxios.get(
      `/api/customers/files/?customer_id=${customer_id}&related_to=${related_to}`
    );
    return { data: response.data };
  } catch (error: any) {
    return { error: renterError(error, "auth") };
  }
};

export const uploadCustomerFiles = async (
  customer_id: number | string,
  files: File[],
  related_to: string,
  onProgress?: (percent: number) => void
): Promise<{ data?: any; error?: string }> => {
  const formData = new FormData();
  formData.append("customer_id", String(customer_id));
  files.forEach((file) => formData.append("files", file));
  formData.append("related_to", related_to);
  try {
    const response = await customAxios.post("/api/customers/files/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (!onProgress || !progressEvent.total) return;
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percent);
      },
    });
    return { data: response.data };
  } catch (error: any) {
    return { error: renterError(error, "auth") };
  }
};
